import { Controller } from '@hotwired/stimulus';
import events from 'lib/events';
import {
  triggerCustomEvent,
  getWindowWidth,
  splitTestEnabled,
  resetUrlWithParam,
  isSearchByImagePage,
} from 'lib/helpers';
import SearchStateManager from 'lib/search-state';
import MediaMatcher from 'lib/media-matcher';
import { FILTERS_HIDE_CLICK, FILTERS_APPLIED_CLICK } from 'components/analytics/conversions/constants/actions';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { initTooltips } from 'ez-styles/assets/js/utils/tooltips';
import Cookies from 'js-cookie';

const mobileWidth = 750;
const searchState = new SearchStateManager();
const mediaMatcher = new MediaMatcher();

export default class SearchFiltersController extends Controller {
  static targets = ['filterOption'];

  filterDefaults = [
    'sort-best',
    'license-all',
    'resolution-hd',
  ];

  filterEvents = [];

  connect() {
    window.addEventListener('search:toggle_search_filters', this.toggleSearchFilters);
    window.addEventListener('search:reset_hidden_filters', this.resetHiddenFilters);

    this.contentType = document.querySelector('[data-search-form-target="input"]').getAttribute('data-content-type');

    this.setupSidebarClassMediaQueries();

    this.setTogglesFromState();
    initTooltips(); // Need to make sure tooltips work every time filters are loaded

    mediaMatcher.evaluateAll();
    mediaMatcher.addListeners();
  }

  disconnect() {
    mediaMatcher.removeListeners();
    window.removeEventListener('search:toggle_search_filters', this.toggleSearchFilters);
    window.removeEventListener('search:reset_hidden_filters', this.resetHiddenFilters);

    if (splitTestEnabled('remove-mobile-filters-apply')) {
      this.setSearchFiltersHiddenKey('mobile-filter-panel', false);
    }
  }

  setupSidebarClassMediaQueries() {
    mediaMatcher.addMediaQuery(`(max-width: ${mobileWidth}px)`,
      false,
      () => {
        const hasActiveSidebar = document.querySelector('body.has-active-mobile-sidebar');
        if (hasActiveSidebar) document.body.classList.remove('has-active-mobile-sidebar');
      });
  }

  setTogglesFromState = () => {
    this.finishedLoading = false;

    const activeTogglesFromState = searchState.getRawParamKeys();

    const allFilterInputs = [
      ...this.element.querySelectorAll('.ez-input--checkbox input'),
    ];

    allFilterInputs.forEach((checkbox) => {
      const checkboxFilterParam = checkbox.closest('.ez-input--checkbox').getAttribute('data-filter-param');

      if (activeTogglesFromState.includes(checkboxFilterParam)) {
        checkbox.checked = true; // eslint-disable-line no-param-reassign
      } else if (!searchState.omittedFromState(checkbox.name)) {
        checkbox.checked = false; // eslint-disable-line no-param-reassign
      }
    });

    this.finishedLoading = true;
  };

  removeFilter = (event) => {
    searchState.removeParamKey(event.detail.filterKey);

    this.applyFilters();
  }

  setFilter = (event = false, inputEl = false) => {
    const filterInput = event ? event.currentTarget : inputEl;
    const thisWrap = filterInput.closest('.ez-input');
    const checkbox = filterInput;
    let param;
    let filterValue;

    if (filterInput.type === 'range') {
      param = `${thisWrap.getAttribute('data-filter-param')}-${this.getFilterRangeValue(thisWrap)}`;
      filterValue = this.getFilterRangeValue(thisWrap);
    } else {
      param = thisWrap.getAttribute('data-filter-param');
      filterValue = (checkbox.checked ? 1 : 0);
    }

    const { key } = thisWrap.dataset;
    const filterEvent = {
      label: key,
      filter_selection: param,
    };

    if (filterInput.type === 'checkbox') {
      filterEvent.value = filterValue;
    }

    this.filterEvents.push(filterEvent);

    if (searchState.omittedFromState(checkbox.name)) {
      this.contentType = checkbox.value;
    } else if (checkbox.checked === true || filterInput.type === 'range') {
      searchState.updateParamKey(param);
    } else {
      searchState.removeParamKey(param);
    }

    if (checkbox.getAttribute('type') === 'radio' && checkbox.checked === false) {
      return;
    }

    if (splitTestEnabled('remove-mobile-filters-apply')) {
      this.applyFilters();
    } else if (getWindowWidth() >= 750) this.applyFilters();
  };

  seoFilterClick = (event) => {
    const ezInput = event.target.closest('.ez-input');
    const radio = ezInput.querySelector('input[type=radio]');

    if (getWindowWidth() < 750) {
      event.preventDefault();
      this.setFilter(false, radio);
    } else {
      // seo filters are on content type filters, need to reset filter state on click
      searchState.resetStateToDefault();
    }

    // if the user clicks an seo link, let's update the radio button visually before the page loads
    const filterSection = event.target.closest('.ez-search-filters__sidebar-section');
    const selected = filterSection.querySelectorAll('.is-selected');
    selected.forEach((el) => {
      el.classList.remove('is-selected');
    });

    radio.checked = true;
    radio.classList.add('is-selected');

    // if the user clicked the fake checkbox, trigger link click.
    if (!event.target.href) event.target.querySelector('a').click();
  }

  getFilterRangeValue = (wrapper) => {
    const rangeAValue = parseFloat(wrapper.querySelector('[data-duration-range-target="rangeA"]').value);
    const rangeBValue = parseFloat(wrapper.querySelector('[data-duration-range-target="rangeB"]').value);

    const minValue = rangeAValue <= rangeBValue ? rangeAValue : rangeBValue;
    const maxValue = rangeAValue > rangeBValue ? rangeAValue : rangeBValue;

    return `${minValue}_${maxValue}`;
  }

  changeFilter(event) {
    const thisWrap = event.target.closest('.ez-input');
    thisWrap.querySelector('input').click();
  }

  applyFilters() {
    if (getWindowWidth() < 750 && splitTestEnabled('remove-mobile-filters-apply')) {
      this.setSearchFiltersHiddenKey('mobile-filter-panel', true);
    }

    this.trackFilterEvents();

    if (isSearchByImagePage() && this.filtersContainContentChange()) {
      searchState.resetStateToDefault();
      resetUrlWithParam('content_type', this.contentType);
    } else {
      triggerCustomEvent(events.search.apply_filters, {
        contentType: this.contentType,
      });
    }

    if (getWindowWidth() < 750) {
      if (!splitTestEnabled('remove-mobile-filters-apply')) {
        this.hideSearchFilters();
      }
    }
  }

  trackFilterEvents() {
    this.filterEvents.forEach((filterEvent) => {
      const event = {
        action: FILTERS_APPLIED_CLICK,
        label: `Filter-${filterEvent.label}`,
        filter_selection: `Filter-${filterEvent.filter_selection}`,
      };

      if (Object.hasOwnProperty.call(filterEvent, 'value')) {
        event.value = filterEvent.value;
      }

      sendTrackingEvent({ data: event });
    });
  }

  toggleSearchFilters = () => {
    const windowWidth = getWindowWidth();
    this.sendShowHideEvent(windowWidth);

    document.body.classList.remove('has-active-sidebar--server');

    if (windowWidth > 750) {
      this.setSearchFiltersHiddenKey('filter-panel', document.body.classList.contains('has-active-sidebar'));
      document.body.classList.toggle('has-active-sidebar');
      triggerCustomEvent('InitAllGrids');
    } else {
      document.body.classList.toggle('has-active-mobile-sidebar');

      if (splitTestEnabled('remove-mobile-filters-apply')) {
        this.setSearchFiltersHiddenKey('mobile-filter-panel', document.body.classList.contains('has-active-mobile-sidebar'));
      }
    }
  }

  filtersContainContentChange = () => {
    for (let i = 0; i < this.filterEvents.length; i += 1) {
      if (this.filterEvents[i].label === 'content') {
        return true;
      }
    }
    return false;
  }

  setSearchFiltersHiddenKey(key, boolean) {
    const searchFiltersCookie = Cookies.get('ezSearchFiltersHidden');
    const searchFiltersHidden = searchFiltersCookie
      ? JSON.parse(searchFiltersCookie)
      : this.buildDefaultHiddenFiltersCookie();

    if (boolean && !searchFiltersHidden.includes(key)) {
      searchFiltersHidden.push(key);
    } else if (!boolean && searchFiltersHidden.includes(key)) {
      searchFiltersHidden.splice(searchFiltersHidden.indexOf(key), 1);
    }

    Cookies.set('ezSearchFiltersHidden', JSON.stringify(searchFiltersHidden));
  }

  buildDefaultHiddenFiltersCookie() {
    const cookie = [];

    const hiddenFilters = document.querySelectorAll('.ez-search-filters__sidebar-section.is-collapsed');

    hiddenFilters.forEach((filter) => {
      cookie.push(filter.dataset.collapseKey);
    });

    return cookie;
  }

  resetHiddenFilters = () => {
    Cookies.remove('ezSearchFiltersHidden');
  }

  hideSearchFilters = () => {
    // remove only, so applying filters on mobile doesn't send GA event.
    document.body.classList.remove('has-active-mobile-sidebar');
    document.body.classList.remove('has-active-sidebar');
  }

  toggleFilterSection(event) {
    const filterSection = event.srcElement.closest('.ez-search-filters__sidebar-section');
    const filterSectionBoolean = !filterSection.classList.contains('is-collapsed');

    filterSection.classList.toggle('is-collapsed');

    this.setSearchFiltersHiddenKey(filterSection.dataset.collapseKey, filterSectionBoolean);
  }

  sendShowHideEvent(windowWidth) {
    const hasActiveSidebar = document.body.classList.contains('has-active-sidebar');
    const hasActiveMobileSidebar = document.body.classList.contains('has-active-mobile-sidebar');
    let action = null;

    if (windowWidth > 750) {
      action = hasActiveSidebar ? 'hide' : 'unhide';
    } else if (windowWidth <= 750) {
      action = hasActiveMobileSidebar ? 'hide' : 'unhide';
    }

    if (
      !document.body.classList.contains('has-active-sidebar--server')
      || action !== 'hide'
    ) {
      return;
    }

    sendTrackingEvent({
      element: this.element,
      data: { action: FILTERS_HIDE_CLICK },
    });
  }

  get searchForm() {
    return document.querySelector("[data-controller~='search-form']");
  }

  get searchTerm() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-term');
    }
    return null;
  }

  get curUserOrder() {
    const curUserOrder = sessionStorage.getItem('user_filter_order') || '[]';

    return JSON.parse(curUserOrder);
  }
}
